import { useEffect, useState } from 'react'
import { useModalControl } from '../../hooks/useModalControl'
import { useFetchDeliveryRegionsQuery } from '../../redux-toolkits/user/user.slice'
import { DeliveryRegionsQuery } from '../../redux-toolkits/locations/locations.type'
import {
  DeliveryTime,
  Vendor,
  VendorDeliveryLocationEntry,
} from '../../redux-toolkits/vendors/vendors.types'
import { ActionChip } from '../cards/actionChip'
import { EditInput } from '../inputs/editInput'
import ComboBox from '../inputs/combobox'
import ModalComponent from '../modal'
import { ConfirmationModal } from '../modal/app_modals/confirmationModal'
import { Button } from '../button'

const deliveryTimeOptions: { name: DeliveryTime; value: DeliveryTime }[] = [
  { name: '30 - 45 mins', value: '30 - 45 mins' },
  { name: '45 mins - 1 hr', value: '45 mins - 1 hr' },
  { name: 'Same day', value: 'Same day' },
  { name: '24 hours', value: '24 hours' },
  { name: '1 - 2 days', value: '1 - 2 days' },
  { name: '3 - 5 days', value: '3 - 5 days' },
  { name: '5 - 7 days', value: '5 - 7 days' },
  { name: 'Under 15 days', value: 'Under 15 days' },
]

type VendorDeliveryRegion = {
  regionId: string
  regionName: string
  deliveryTime: DeliveryTime
}

type VendorFulfilmentDeliveryLocation = {
  locationId: string
  locationName: string
  deliveryTime: DeliveryTime
}

function getLocationId(locationId: VendorDeliveryLocationEntry['locationId']) {
  return typeof locationId === 'string' ? locationId : locationId?._id
}

function getLocationName(
  locationId: VendorDeliveryLocationEntry['locationId'],
) {
  return typeof locationId === 'string' ? locationId : locationId?.name
}

function mapVendorRegions(vendor?: Vendor): VendorDeliveryRegion[] {
  return (
    vendor?.deliveryLocation
      ?.filter((location) => location.locationType === 'region')
      .map((location) => ({
        regionId: getLocationId(location.locationId) ?? '',
        regionName: getLocationName(location.locationId) ?? '',
        deliveryTime: (location.deliveryTime || '24 hours') as DeliveryTime,
      }))
      .filter((location) => location.regionId) ?? []
  )
}

function mapVendorFulfilmentDeliveryLocations(
  vendor?: Vendor,
): VendorFulfilmentDeliveryLocation[] {
  return (
    vendor?.deliveryLocation
      ?.filter((location) => location.locationType === 'pickup')
      .map((location) => ({
        locationId: getLocationId(location.locationId) ?? '',
        locationName: getLocationName(location.locationId) ?? '',
        deliveryTime: (location.deliveryTime || '24 hours') as DeliveryTime,
      }))
      .filter((location) => location.locationId) ?? []
  )
}

const VendorDeliveryRegionModal = ({
  open,
  setOpen,
  region,
  loading,
  onCloseCallback,
  onSuccessCallback,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  region?: VendorDeliveryRegion
  loading?: boolean
  onCloseCallback: () => void
  onSuccessCallback: (region: VendorDeliveryRegion) => void
}) => {
  const [query, setQuery] = useState<DeliveryRegionsQuery>({
    page: 1,
    limit: 1000,
    search: undefined,
    isAvailable: true,
  })
  const [selectedRegion, setSelectedRegion] = useState<VendorDeliveryRegion>({
    regionId: '',
    regionName: '',
    deliveryTime: '24 hours',
  })
  const [error, setError] = useState('')

  const { data: regionsData, isFetching } = useFetchDeliveryRegionsQuery(
    query,
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    setSelectedRegion(
      region ?? {
        regionId: '',
        regionName: '',
        deliveryTime: '24 hours',
      },
    )
    setError('')
  }, [region, open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={onCloseCallback}
      title={region ? 'Edit delivery region' : 'Add delivery region'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-4">
        <ComboBox
          type="text"
          name="regionName"
          placeholder="Delivery region"
          value={selectedRegion.regionName}
          onChange={(event) => {
            setSelectedRegion({
              ...selectedRegion,
              regionId: '',
              regionName: event.target.value,
            })
          }}
          hasIcon={false}
          errors={error}
          menuItems={regionsData?.data.docs
            .filter((deliveryRegion) => !deliveryRegion.parentRegion)
            .map((deliveryRegion) => {
              return {
                name: deliveryRegion.name,
                value: {
                  name: deliveryRegion.name,
                  value: deliveryRegion._id,
                },
              }
            })}
          onSelectOption={(selected) => {
            setSelectedRegion({
              ...selectedRegion,
              regionId: selected.value,
              regionName: selected.name,
            })
            setError('')
          }}
          onSearch={(val) => {
            setQuery({
              ...query,
              search: val || undefined,
              page: 1,
            })
          }}
          loading={isFetching}
          immediate
          position="bottom start"
          menuClassName="!max-h-[250px] shadow-md !z-[999]"
          className="w-full"
          isDropdown
        />

        <div className="flex flex-col gap-2">
          <p className="text-base font-medium text-black">Delivers in</p>
          <select
            className="h-[56px] w-full rounded-md border border-divider-gray bg-white px-4 text-base text-black outline-none focus:border-black"
            name="deliveryTime"
            value={selectedRegion.deliveryTime}
            onChange={(event) => {
              setSelectedRegion({
                ...selectedRegion,
                deliveryTime: event.target.value as DeliveryTime,
              })
            }}
          >
            {deliveryTimeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <Button
          label="Proceed"
          type="button"
          className="mt-6"
          loading={loading}
          disabled={loading}
          primary
          onClick={() => {
            if (!selectedRegion.regionId) {
              setError('Select a delivery region.')
              return
            }

            onSuccessCallback(selectedRegion)
          }}
        />
      </div>
    </ModalComponent>
  )
}

export const VendorDeliveryRegions = ({
  vendor,
  loading,
  onSuccess,
  className,
}: {
  vendor?: Vendor
  loading: boolean
  onSuccess: (
    data: {
      regions: VendorDeliveryRegion[]
    },
    closeCallback: () => void,
  ) => void
  className?: string
}) => {
  const [selectedRegion, setSelectedRegion] = useState<VendorDeliveryRegion>()
  const [selectedRegionIndex, setSelectedRegionIndex] = useState<number>()
  const [deliveryRegions, setDeliveryRegions] = useState<
    VendorDeliveryRegion[]
  >([])
  const fulfilmentDeliveryLocations =
    mapVendorFulfilmentDeliveryLocations(vendor)

  const { setModalOpen, handleModalClose, handleModalOpen, modal, modalOpen } =
    useModalControl()

  useEffect(() => {
    setDeliveryRegions(mapVendorRegions(vendor))
  }, [vendor])

  const getDeliveryRegionPayload = (regions = deliveryRegions) => regions

  return (
    <>
      <EditInput
        title="Available Locations"
        onEditSave={() => {}}
        editing={false}
        onEditClick={() => {
          setSelectedRegion(undefined)
          setSelectedRegionIndex(undefined)
          handleModalOpen('selectDeliveryRegionModal')
        }}
        onCancel={() => {}}
        loading={false}
        className={className}
      >
        {() => {
          return (
            <div className="flex flex-wrap gap-4 items-center">
              {deliveryRegions.length < 1 &&
              fulfilmentDeliveryLocations.length < 1 ? (
                <p className="text-sec-black">No available location added</p>
              ) : (
                <>
                  {fulfilmentDeliveryLocations.map((location) => (
                    <ActionChip
                      key={`delivery-fc-${location.locationId}`}
                      name={`${location.locationName} in ${location.deliveryTime}`}
                    />
                  ))}
                  {deliveryRegions.map((region, idx) => {
                    return (
                      <ActionChip
                        key={`delivery-region-${region.regionId}`}
                        name={`${region.regionName} in ${region.deliveryTime}`}
                        onClick={() => {
                          setSelectedRegion(region)
                          setSelectedRegionIndex(idx)
                          handleModalOpen('selectDeliveryRegionModal')
                        }}
                        onDismiss={() => {
                          setSelectedRegion(region)
                          handleModalOpen('removeDeliveryRegionModal')
                        }}
                      />
                    )
                  })}
                </>
              )}
            </div>
          )
        }}
      </EditInput>

      <ConfirmationModal
        open={modalOpen && modal === 'removeDeliveryRegionModal'}
        setOpen={setModalOpen}
        message={'Are you sure you want to proceed?'}
        title="Confirm Removal"
        loading={loading}
        confirmText="Remove"
        onCloseCallback={() => {}}
        onConfirm={async () => {
          const nextRegions = deliveryRegions.filter(
            (region) => region.regionId !== selectedRegion?.regionId,
          )

          onSuccess(
            {
              regions: getDeliveryRegionPayload(nextRegions),
            },
            () => {
              setDeliveryRegions(nextRegions)
              setSelectedRegion(undefined)
              handleModalClose()
            },
          )
        }}
      />

      <VendorDeliveryRegionModal
        open={modalOpen && modal === 'selectDeliveryRegionModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          setSelectedRegion(undefined)
          setSelectedRegionIndex(undefined)
          handleModalClose()
        }}
        onSuccessCallback={async (region) => {
          const nextRegions = [...deliveryRegions]

          if (selectedRegionIndex !== undefined) {
            nextRegions[selectedRegionIndex] = region
          } else {
            const existingIndex = nextRegions.findIndex(
              (item) => item.regionId === region.regionId,
            )

            if (existingIndex >= 0) {
              nextRegions[existingIndex] = region
            } else {
              nextRegions.push(region)
            }
          }

          onSuccess(
            {
              regions: getDeliveryRegionPayload(nextRegions),
            },
            () => {
              setDeliveryRegions(nextRegions)
              setSelectedRegion(undefined)
              setSelectedRegionIndex(undefined)
              handleModalClose()
            },
          )
        }}
        region={selectedRegion}
        loading={loading}
      />
    </>
  )
}
